<script setup lang="ts">
import type { Project } from '~/types/project.type'
import type { Task } from '~/types/task.type'
import type { TaskList } from '~/types/taskList.type'
import type { TimeTrackerFormModel } from '~/types/timeTracker.type'
import { unknownError } from '~/constants'

const props = defineProps({
  projects: {
    type: Array as PropType<Array<Project<'withClient'>>>,
    default: () => [],
  },
  taskLists: {
    type: Array as PropType<Array<TaskList<'withTasks'>>>,
    default: undefined,
  },
  title: {
    type: String,
    default: 'Zeitgeber',
  },
})

/* End of tasks */

defineEmits<{ close: [boolean] }>()

const timeTracker = useTimeTracker()

const {
  isRunning,
  activeTimeEntry,
  timeTrackerModel,
  edited,
  timeEntryCreateError,
  timeEntryPendingError,
  timeEntryUpdateError,
  timeEntryUpdateStatus,
  timeEntryStopError,
} = storeToRefs(timeTracker)

const toast = useToast()

/* Start of form handling */

const { formRef, rules, onSubmit, apiErrors } = useNaiveForm(timeTrackerModel)
const { apiErrorMessages, clearApiErrors } = useApiErrors<TimeTrackerFormModel>(
  apiErrors,
  timeEntryCreateError,
  timeEntryPendingError,
  timeEntryUpdateError,
  timeEntryStopError,
)

watch(() => timeTracker.isRunning, (isRunning) => {
  if (isRunning) {
    timeTrackerModel.value = {
      taskId: activeTimeEntry.value!.taskId,
      notes: activeTimeEntry.value!.notes,
      projectId: activeTimeEntry.value!.projectId,
      taskListId: activeTimeEntry.value!.task.taskListId,
    }
  }
  else {
    timeTrackerModel.value.notes = ''
  }

  timeTracker.resetModelState()
})

rules.value = {
  projectId: [
    {
      required: true,
      message: 'Bitte ein Projekt auswählen',
    },
    {
      validator: () => !apiErrors.value.projectId,
      message: () => apiErrorMessages.value.projectId!,
      trigger: ['input', 'blur'],
    },
  ],
  taskId: [
    {
      required: true,
      message: 'Bitte eine Aufgabe auswählen',
    },
    {
      validator: () => !apiErrors.value.taskId,
      message: () => apiErrorMessages.value.taskId!,
      trigger: ['input', 'blur'],
    },
  ],
}

async function submitForm() {
  clearApiErrors()

  if (!isRunning.value) {
    onSubmit(async () => {
      await timeTracker.start()
    })
  }
  else if (edited.value) {
    onSubmit(async () => {
      const { error } = await timeTracker.updateActiveTimeEntry({
        taskId: timeTrackerModel.value.taskId!,
        notes: timeTrackerModel.value.notes,
      })

      if (error.value)
        toast.add({ color: 'error', description: error.value.data?.message ?? unknownError })
      else
        timeTracker.refreshInitialModelState()
    })
  }
  else {
    await timeTracker.stop()
  }
}

useMetaSShortcut(submitForm)

/* End of form handling */

/* Start of tasks */

const taskListsFilter = computed<ApiTaskListGetListParams & { withTasks: true, withCompleted: false }>(() => {
  return {
    projectId: timeTrackerModel.value.projectId ?? 0,
    withTasks: true,
    withCompleted: false,
  }
})

const { data: taskListsData, status: tasksStatus, refresh: fetchTaskLists } = useApiTaskListGetList(taskListsFilter, { immediate: false, watch: false })

async function onProjectSelect() {
  timeTrackerModel.value.taskId = null // clear task selection due to new project
  await fetchTaskLists()
}

const taskLists = computed(() => {
  return taskListsData.value?.data ?? props.taskLists ?? []
})

async function onTaskCreated(task: Task<undefined>) {
  await fetchTaskLists()
  timeTrackerModel.value.taskId = task.id
  formRef.value?.validate(undefined, rule => rule.key === 'taskId')
}

const clipboardStore = useClipboardStore()
const {
  task: copiedTask,
  project: copiedProject,
  taskList: copiedTaskList,
} = storeToRefs(clipboardStore)

const isProjectPasted = refAutoReset(false, 1500)
const isTaskPasted = refAutoReset(false, 1500)

async function onPaste(event?: ClipboardEvent) {
  formRef.value?.restoreValidation()

  if (copiedTask.value) {
    event?.preventDefault()
    event?.stopPropagation()
    if (!props.projects.some(project => project.id === copiedTask.value?.taskList.projectId)) {
      toast.add({ color: 'error', title: 'Projekt nicht gefunden!', description: 'Das kopierte Projekt konnte nicht gefunden werden! Ist es bereits abgeschlossen?' })
      return
    }

    timeTrackerModel.value.projectId = copiedTask.value.taskList.projectId
    isTaskPasted.value = true
    await onProjectSelect()

    if (!taskLists.value.some(taskList => taskList.id === copiedTask.value?.taskListId && taskList.tasks.some(task => task.id === copiedTask.value?.id))) {
      toast.add({ color: 'error', title: 'Aufgabe nicht gefunden!', description: 'Die kopierte Aufgabe konnte nicht gefunden werden! Ist die Aufgabe oder Aufgabenliste bereits abgeschlossen?' })
      return
    }

    timeTrackerModel.value.taskListId = copiedTask.value.taskList.id
    timeTrackerModel.value.taskId = copiedTask.value.id
  }

  else if (copiedProject.value ?? copiedTaskList.value) {
    event?.preventDefault()
    event?.stopPropagation()
    const projectId = copiedTaskList.value?.projectId ?? copiedProject.value?.id ?? -1
    if (!props.projects.some(project => project.id === projectId)) {
      toast.add({ color: 'error', title: 'Projekt nicht gefunden!', description: 'Das kopierte Projekt konnte nicht gefunden werden! Ist es bereits abgeschlossen?' })
      return
    }

    timeTrackerModel.value.projectId = projectId
    isProjectPasted.value = true

    await onProjectSelect()
  }
}

const selectedProject = computed(() => {
  return props.projects.find(project => project.id === timeTrackerModel.value.projectId)
})

function resetModel() {
  const currentProjectId = timeTrackerModel.value.projectId
  timeTracker.resetModelState()
  if (currentProjectId !== timeTrackerModel.value.projectId)
    fetchTaskLists()
}

const showGlobalSearch = ref(false)

async function onGlobalSearchProjectSelected(project: Project<'withClient'>) {
  timeTrackerModel.value.projectId = project.id
  await onProjectSelect()
}

async function onGlobalSearchTaskListSelected(taskList: TaskList<'withProject'>) {
  timeTrackerModel.value.projectId = taskList.projectId
  await onProjectSelect()
}

async function onGlobalSearchTaskSelected(task: Task<'withTaskList'>) {
  timeTrackerModel.value.projectId = task.taskList.projectId
  await onProjectSelect()
  timeTrackerModel.value.taskId = task.id
}
</script>

<template>
  <UModal
    :title="title"
    :close="{ onClick: () => $emit('close', false) }"
    :dismissible="false"
  >
    <template #body>
      <NForm
        ref="formRef"
        :model="timeTrackerModel"
        :rules="rules"
        class="max-w-full"
        @submit.prevent="submitForm"
        @paste="onPaste"
      >
        <NFormItem label="Projekt" path="projectId">
          <NInputGroup class="w-full">
            <ProjectSelect
              v-model="timeTrackerModel.projectId"
              :projects="projects"
              show-recent-projects-group
              @update:value="onProjectSelect"
            />
            <NTooltip v-if="copiedProject ?? copiedTaskList?.project">
              <template #trigger>
                <NButton @click="() => onPaste()">
                  <template #icon>
                    <UIcon :name="isProjectPasted ? 'i-ic-outline-fact-check' : 'i-ic-baseline-content-paste-go'" />
                  </template>
                </NButton>
              </template>
              <span>Projekt "{{ (copiedProject ?? copiedTaskList?.project)!.name }}" aus Zwischenablage übernehmen</span>
            </NTooltip>
          </NInputGroup>
        </NFormItem>

        <NFormItem label="Aufgabe" path="taskId">
          <NInputGroup class="w-full">
            <TaskSelect
              v-model:value="timeTrackerModel.taskId"
              :project-id="timeTrackerModel.projectId"
              :project="selectedProject"
              :task-lists="taskLists"
              :select-props="{
                loading: tasksStatus === 'pending',
                disabled: timeTrackerModel.projectId === null || tasksStatus === 'pending',
              }"
              :show-task-add-action="timeTrackerModel.projectId !== null"
              @task-created="onTaskCreated"
            />
            <NTooltip v-if="copiedTask">
              <template #trigger>
                <NButton @click="() => onPaste()">
                  <template #icon>
                    <UIcon :name="isTaskPasted ? 'i-ic-outline-fact-check' : 'i-ic-baseline-content-paste-go'" />
                  </template>
                </NButton>
              </template>
              <span>Aufgabe "{{ copiedTask.name }}" aus Zwischenablage übernehmen</span>
            </NTooltip>
          </NInputGroup>
        </NFormItem>

        <div class="flex justify-end">
          <GlobalSearch
            v-model:show="showGlobalSearch"
            select-mode
            :show-reports="false"
            :show-clients="false"
            :disable-shortcut-in-modals="false"
            :include-completed-and-archived-resources="false"
            @project-selected="onGlobalSearchProjectSelected"
            @task-list-selected="onGlobalSearchTaskListSelected"
            @task-selected="onGlobalSearchTaskSelected"
          >
            <template #trigger>
              <NTooltip>
                <template #trigger>
                  <NButton type="default" secondary size="small" @click="showGlobalSearch = true">
                    Projekt/Aufgabe suchen
                    <template #icon>
                      <UIcon name="i-ic-baseline-search" />
                    </template>
                  </NButton>
                </template>
                Tippe <UKbd>/</UKbd>, um direkt nach Aufgaben/Aufgabenlisten oder Projekten zu suchen
              </NTooltip>
            </template>
          </GlobalSearch>
        </div>

        <NFormItem label="Anmerkungen zum Zeiteintrag" path="notes">
          <NInput
            v-model:value="timeTrackerModel.notes"
            type="textarea"
            placeholder="Optionale Anmerkungen"
            :rows="8"
            :resizable="false"
          />
        </NFormItem>

        <Stopwatch v-if="!isRunning || !edited" />
        <div v-else class="flex items-center">
          <NButtonGroup>
            <NTooltip>
              <template #trigger>
                <NButton attr-type="submit" type="primary" :loading="timeEntryUpdateStatus === 'pending'">
                  Aktualisieren
                </NButton>
              </template>
              <ShortcutDisplaySave />
            </NTooltip>
            <NButton @click="resetModel">
              Abbrechen
            </NButton>
          </NButtonGroup>
        </div>
      </NForm>
    </template>
  </UModal>
</template>
